<template>
  <VLoader v-if="videosLoading" />
  <div v-else class="w-full h-full">
    <div>
      <div class="h-px my-6 bg-ricoma-gray-300"></div>
      <div>
        <h3 class="px-8 mb-8 text-sm font-semibold">Videos</h3>
        <VideoCards :videos="videos" :serie="serie" @delete="handleDelete" @edit="handleEdit" />

        <!-- Modals -->
        <ModalPlus
          title="Add Video"
          buttonText="Add Video"
          :show="showModal"
          :onClose="handleClose"
          :onProceed="handleSubmit(handleAddVideo)"
          :loading="formLoading"
          maxWidth="900px"
        >
          <div class="py-8 pb-4">
            <VideoForm :errors="errors" @set="handleSetField" :showIntro="false" />
          </div>
        </ModalPlus>
      </div>
      <div class="h-px my-6 bg-ricoma-gray-300"></div>
      <div class="flex justify-between">
        <h3 class="px-8 mb-8 text-sm font-semibold">Resources</h3>
        <button
          class="flex items-center justify-center h-12 px-5 text-sm text-white uppercase bg-gray-900 rounded"
          @click="showModalResource = true"
        >
          <VSvgIcon name="plus" class="mr-1" />
          Add Resources
        </button>
      </div>
      <div>
        <ResourcesTable
          :resources="resources"
          :serie="serie"
          @delete="handleDeleteResources"
          @edit="handleSetEditableResource"
        />

        <!-- Modals -->
        <ModalPlus
          title="Add Resource"
          buttonText="Add Resource"
          :show="showModalResource"
          :onClose="handleCloseResources"
          :onProceed="handleSubmit(handleProceedResource)"
          :loading="formLoading"
          maxWidth="900px"
        >
          <div class="py-8 pb-4">
            <ResourcesForm
              :initialValues="editableResource"
              :errors="errors"
              @set="handleSetField"
            />
          </div>
        </ModalPlus>
      </div>
    </div>
  </div>
</template>

<script>
import { formMixin, videosViewMixin } from '@/mixins';

import VLoader from '@/components/base/VLoader';
import ModalPlus from '@/components/shared/modal/ModalPlus';
import VideoCards from '@/components/admin/VideoCards';
import VideoForm from '@/components/admin/VideoForm';
import ResourcesTable from '@/components/admin/ResourcesTable';
import ResourcesForm from '@/components/admin/ResourcesForm';
import VSvgIcon from '@/components/base/VSvgIcon';

export default {
  name: 'AdminPostTrainingVideosView',
  components: {
    VLoader,
    VideoCards,
    VideoForm,
    ModalPlus,
    VSvgIcon,
    ResourcesTable,
    ResourcesForm,
  },
  mixins: [
    formMixin({ title: '', link: '', file: '' }, ['link', 'file']),
    videosViewMixin('post-training-videos'),
  ],
  data() {
    return {
      showModalResource: false,
      editableResource: {},
      resources: [],
    };
  },
  watch: {
    async language({ code }) {
      this.videos = await this.fetchVideos(this.$http, this.$route.params.id, code);
    },
  },
  computed: {
    editable() {
      return this.editableResource && !!Object.keys(this.editableResource).length;
    },
  },
  methods: {
    filterResources(propName, propValue) {
      return data => data[propName] !== propValue;
    },
    async handleAddVideo(data) {
      const serieId = this.$route.params.id;

      const requestData = {
        ...data,
        group_id: serieId,
        lang_code: this.language.code,
      };

      const formData = new FormData();
      Object.keys(requestData).forEach(key => formData.append(key, requestData[key]));

      const res = await this.$http.post(`/admin/post-training-videos`, formData);
      this.videos = this.videos.concat(res.data.data);

      this.handleResetFields();
      this.handleClose();
    },
    handleSetEditableResource(resource) {
      this.editableResource = resource;
      this.showModalResource = true;
    },
    handleProceedResource(data) {
      this.editable
        ? this.handleUpdateResource({ id: this.editableResource.id, data })
        : this.handleAddResources(data);
    },
    handleCloseResources() {
      this.editableResource = {};
      this.showModalResource = false;
    },
    async handleAddResources(data) {
      const serieId = this.$route.params.id;

      const requestData = {
        ...data,
        group_id: serieId,
        lang_code: this.language.code,
      };

      const formData = new FormData();
      Object.keys(requestData).forEach(key => formData.append(key, requestData[key]));

      const res = await this.$http.post(
        `/admin/post-training-videos/${serieId}/resources`,
        formData,
      );
      this.resources = this.resources.concat(res.data.data);
      this.handleCloseResources();
    },

    async handleDeleteResources(id) {
      const serieId = this.$route.params.id;
      await this.$http.delete(`/admin/post-training-videos/${serieId}/resources/${id}`);
      this.resources = this.resources.filter(this.filterResources('id', id));
    },

    async handleUpdateResource({ id, data }) {
      const serieId = this.$route.params.id;

      const requestData = {
        ...data,
        group_id: serieId,
        lang_code: this.language.code,
      };

      const formData = new FormData();
      Object.keys(requestData).forEach(key => formData.append(key, requestData[key]));

      const res = await this.$http.post(
        `/admin/post-training-videos/${serieId}/resources/${id}`,
        formData,
      );
      const resource = res.data.data;

      // update resources array with new changes
      this.resources = this.resources.map(r => (r.id === resource.id ? resource : r));
      this.handleCloseResources();
    },
    async getResources() {
      const serieId = this.$route.params.id;
      const rest = await this.$http.get(`/admin/post-training-videos/${serieId}/resources`);
      this.resources = rest.data.data;
    },
  },
  mounted() {
    this.getResources();
  },
};
</script>
